'use client';

import { useMemo, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import { Box } from '@terra/ui-styled-system';
import { Tooltip } from '../../../components/tooltip';
import { useThemeMode } from '../../theme/useThemeMode';
import {
  terraDarkTheme,
  terraLightTheme,
} from '../../http-editor/codemirror/terra-theme';
import { Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import { ansiEscapeHighlighting } from './ansi-escape-highlighting';
import { stripAnsiEscapes } from './ansi-escape-parser';

export type ReadOnlyCodeLanguage =
  | 'python'
  | 'javascript'
  | 'json'
  | 'html'
  | 'ansi'
  | 'text';
type ReadOnlyCodeEditorProps = {
  value: string;
  maxHeight?: string;
  minHeight?: string;
  showCopyButton?: boolean;
  showLineNumbers?: boolean;
  language?: ReadOnlyCodeLanguage;
  borderless?: boolean;
  transparent?: boolean;
};
const normalizeLineEndings = (value: string): string =>
  value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
const transparentEditorTheme = EditorView.theme({
  '&': {
    backgroundColor: 'transparent',
  },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    border: 'none',
  },
  '.cm-activeLine, .cm-activeLineGutter': {
    backgroundColor: 'transparent',
  },
});

export function ReadOnlyCodeEditor({
  value,
  maxHeight = '300px',
  minHeight = '44px',
  showCopyButton = true,
  showLineNumbers = false,
  language = 'text',
  borderless = false,
  transparent,
}: ReadOnlyCodeEditorProps) {
  const { mode } = useThemeMode();
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isTransparent = transparent ?? borderless;
  const normalizedValue = useMemo(() => normalizeLineEndings(value), [value]);
  const isDarkMode = mode === 'dark';
  const editorTheme = isDarkMode ? terraDarkTheme : terraLightTheme;
  const copyValue = useMemo(
    () =>
      language === 'ansi' ? stripAnsiEscapes(normalizedValue) : normalizedValue,
    [language, normalizedValue],
  );
  const languageExtension = useMemo(() => {
    switch (language) {
      case 'python':
        return python();
      case 'javascript':
        return javascript({ jsx: true, typescript: true });
      case 'json':
        return json();
      case 'html':
        return html();
      case 'ansi':
        return ansiEscapeHighlighting(isDarkMode ? 'dark' : 'light');
      case 'text':
        return null;
      default: {
        const unsupportedLanguage: never = language;

        return unsupportedLanguage;
      }
    }
  }, [isDarkMode, language]);
  const extensions = useMemo(() => {
    const baseExtensions = [
      EditorState.readOnly.of(true),
      EditorView.lineWrapping,
      editorTheme,
      ...(isTransparent ? [transparentEditorTheme] : []),
    ];

    if (!languageExtension) {
      return baseExtensions;
    }

    return [...baseExtensions, languageExtension];
  }, [editorTheme, isTransparent, languageExtension]);
  const handleCopy = () => {
    navigator.clipboard
      .writeText(copyValue)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch((error) => {
        console.error('Failed to copy code:', error);
      });
  };

  return (
    <Box
      bg={isTransparent ? 'transparent' : 'bg.canvas'}
      borderRadius={borderless ? '0' : 'sm'}
      borderWidth={borderless ? '0' : '1px'}
      borderColor={borderless ? 'transparent' : 'border.subtle'}
      width="100%"
      h="full"
      position="relative"
      overflow="hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showCopyButton && (
        <Box
          position="absolute"
          top={1}
          right={4}
          zIndex={1}
          opacity={isHovered ? 1 : 0}
          pointerEvents={isHovered ? 'auto' : 'none'}
        >
          <Tooltip title="Copy code">
            <Box
              as="button"
              aria-label="Copy code"
              onClick={handleCopy}
              h="5"
              w="5"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              color="fg.muted"
              borderRadius="xs"
              _hover={{
                color: 'fg.default',
                bg: 'bg.subtle',
              }}
            >
              {copied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
            </Box>
          </Tooltip>
        </Box>
      )}
      <Box maxHeight={maxHeight} minHeight={minHeight} overflow="auto">
        <CodeMirror
          value={normalizedValue}
          extensions={extensions}
          basicSetup={{
            lineNumbers: showLineNumbers,
            foldGutter: false,
            highlightActiveLine: false,
            highlightSelectionMatches: false,
            highlightSpecialChars: language !== 'ansi',
            autocompletion: false,
          }}
          theme="none"
          editable={false}
        />
      </Box>
    </Box>
  );
}
